import { SalaryType } from "model/Salary";
import { isSettingEnabled } from "model/settings.logic";
import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";
import type { EmploymentUiRules } from "../../profilePanel.contract";

interface CompanySettingValue {
  key: string;
  value?: string | null;
}

/**
 * Employment form rules:
 * 1. Simflexity employee must use hourly salary type (Hourly).
 * 2. Simflexity employee must use SimflexityPay as payment type.
 * 3. Consultant and trainee must use employee category = "Undefined".
 * 4. SimflexityPay may be selected for other employment types only when:
 *    - the company has a Flex subscription,
 *    - SimflexityPay is enabled in company settings,
 *    - salary type is Hourly.
 * 5. If SimflexityPay is not allowed in the current context, payment type is normalized to "None".
 * 6. End date is required on save for project employees and trainees.
 */

/**
 * Derives UI rule flags for employment/salary form behavior from rule set above.
 */
export const toEmploymentUiRules = (
  employmentType: string,
  salaryType: string,
  subscription: string | undefined,
  companySettings: CompanySettingValue[],
): EmploymentUiRules => {
  const isSimflexityEmployee =
    employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE;
  const isTraineeEmployee = employmentType === EMPLOYMENT_TYPE_TRAINEE;
  const isConsultantEmployee = employmentType === EMPLOYMENT_TYPE_CONSULTANT;
  const isProjectEmployee = employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE;
  const isHourlyTemporaryEmployee =
    employmentType === "TemporaryWorker" ||
    employmentType === "ExclusiveTemporaryWorker";
  const isEndDateRequiredOnSave = isProjectEmployee || isTraineeEmployee;
  const isFlexCompany = subscription?.includes("Flex") === true;

  const hasSimflexityPay = isSettingEnabled(companySettings, "SimflexityPay");

  const canSelectSimflexityPay = isSimflexityEmployee
    ? true
    : isFlexCompany && hasSimflexityPay && salaryType === SalaryType.Hourly;

  return {
    isSimflexityEmployee,
    isTraineeEmployee,
    isConsultantEmployee,
    isProjectEmployee,
    isHourlyTemporaryEmployee,
    isEndDateRequiredOnSave,
    canSelectSimflexityPay,
  };
};

/**
 * Applies the derived employment rules to current form values and returns a normalized copy.
 *
 * What this function may change:
 * - `salaryType`: forced to `Hourly` for Simflexity employees.
 * - `employeeCategory`: forced to `"Undefined"` for consultants and trainees.
 * - `paymentType`:
 *   - forced to `"SimflexityPay"` for Simflexity employees,
 *   - reset to `"None"` when SimflexityPay is selected but not allowed by rules.
 *
 * What this function does not do:
 * - It does not mutate the input object.
 * - It does not validate required fields or return errors.
 * - It only enforces cross-field consistency based on `rules`.
 */
export const withNormalizedEmploymentFormByRules = (
  employmentDetails: {
    salaryType: string;
    employeeCategory: string;
    paymentType: string;
  },
  rules: EmploymentUiRules,
): {
  salaryType: string;
  employeeCategory: string;
  paymentType: string;
} => {
  let nextSalaryType = employmentDetails.salaryType;
  let nextEmployeeCategory = employmentDetails.employeeCategory;
  let nextPaymentType = employmentDetails.paymentType;

  if (rules.isSimflexityEmployee && nextSalaryType !== SalaryType.Hourly) {
    nextSalaryType = SalaryType.Hourly;
  }

  if (rules.isConsultantEmployee || rules.isTraineeEmployee) {
    nextEmployeeCategory = "Undefined";
  }

  if (rules.isSimflexityEmployee && nextPaymentType !== "SimflexityPay") {
    nextPaymentType = "SimflexityPay";
  }

  if (
    !rules.isSimflexityEmployee &&
    nextPaymentType === "SimflexityPay" &&
    !rules.canSelectSimflexityPay
  ) {
    nextPaymentType = "None";
  }

  return {
    salaryType: nextSalaryType,
    employeeCategory: nextEmployeeCategory,
    paymentType: nextPaymentType,
  };
};
